import { useEffect, useMemo, useRef, useState } from 'react';

import { Loader } from '@/components/Loader';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/ui/status-chip';
import ChatMessage from '@/sections/insights/aiAssistant/ChatMessage';
import {
  useHuntMessages,
  useSendHuntMessage,
} from '@/sections/insights/hunts/useHunts';
import { groupMessagesByToolUseId } from '@/utils/toolMessageGrouping';

interface ConversationViewProps {
  convId: string;
  isRunning: boolean;
}

/**
 * Renders one hunt iteration: scrollable transcript on top, guidance
 * textarea below. The textarea is only mounted when the conversation
 * is the hunt's currently-running iteration — messages sent here are
 * picked up by the agent at its next tool turn.
 */
export function ConversationView({ convId, isRunning }: ConversationViewProps) {
  const { data: messages = [], isLoading } = useHuntMessages(convId);
  const { mutate: send, isPending: sending } = useSendHuntMessage(convId);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const grouped = useMemo(() => groupMessagesByToolUseId(messages), [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  return (
    <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3">
        {isLoading && messages.length === 0 ? (
          <Loader isLoading type="spinner" />
        ) : messages.length === 0 ? (
          <div className="text-chariot-text-secondary text-sm">
            No messages yet.
          </div>
        ) : (
          grouped.map(item => {
            if (item.type === 'message') {
              return <ChatMessage key={item.data.id} message={item.data} />;
            }
            if (item.data.call) {
              return (
                <ChatMessage
                  key={item.data.call.id}
                  message={item.data.call}
                  pairedResponse={item.data.response}
                />
              );
            }
            if (item.data.response) {
              return (
                <ChatMessage
                  key={item.data.response.id}
                  message={item.data.response}
                />
              );
            }
            return null;
          })
        )}
      </div>
      <div className="border-chariot-stroke border-t p-3">
        {isRunning ? (
          <>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="Send guidance to the running agent… (drained at the next tool turn)"
              className="border-chariot-stroke bg-chariot-background-primary focus:ring-chariot-brand-primary w-full rounded-md border p-2 text-xs focus:ring-2 focus:outline-hidden focus:ring-inset"
              rows={3}
            />
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                disabled={!draft.trim() || sending}
                onClick={() => {
                  send({ message: draft }, { onSuccess: () => setDraft('') });
                }}
              >
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </>
        ) : (
          <div className="text-chariot-text-secondary flex items-center gap-1 text-xs italic">
            <span>
              This conversation is not currently running. Messages can only be
              sent to the active iteration (shown with a
            </span>
            <StatusChip label="Running" variant="info" />
            <span>badge).</span>
          </div>
        )}
      </div>
    </>
  );
}
